import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface BatchResult {
  ok: boolean;
  commit?: string;
  offset?: number;
  processed?: number;
  next_offset?: number | null;
  done?: boolean;
  counts?: {
    stylesThisBatch: number;
    localesThisBatch: number;
    skippedParseFail: number;
  };
  upload_failures?: Array<{ path: string; error: string }>;
  catalog_errors?: string[];
  elapsed_ms?: number;
  error?: string;
}

/** Resumable CSL Style Repository sync — admin-only.
 *  Loops batches of `limit` candidates until `done:true`. The first
 *  batch fetches the latest commit SHA; subsequent batches pin to it
 *  so the whole sync sees one consistent upstream snapshot. */
export const CslStylesSyncCard = () => {
  const [running, setRunning] = useState(false);
  const [limit, setLimit] = useState(300);
  const [batches, setBatches] = useState<BatchResult[]>([]);
  const [totals, setTotals] = useState({ styles: 0, locales: 0, skipped: 0, ms: 0 });

  const invoke = async (body: Record<string, unknown>): Promise<BatchResult> => {
    const { data, error } = await supabase.functions.invoke("csl-styles-sync", { body });
    if (error) return { ok: false, error: error.message };
    return data as BatchResult;
  };

  const runDryRun = async () => {
    setRunning(true);
    setBatches([]);
    setTotals({ styles: 0, locales: 0, skipped: 0, ms: 0 });
    let offset = 0;
    let commitSha: string | undefined;
    const collected: BatchResult[] = [];
    const running = { styles: 0, locales: 0, skipped: 0, ms: 0 };
    try {
      while (true) {
        const res = await invoke({ dry_run: true, offset, limit, commit_sha: commitSha });
        collected.push(res);
        setBatches([...collected]);
        if (!res.ok && res.error) { toast.error(`Batch failed: ${res.error}`); break; }
        commitSha = res.commit;
        running.styles += res.counts?.stylesThisBatch ?? 0;
        running.locales += res.counts?.localesThisBatch ?? 0;
        running.skipped += res.counts?.skippedParseFail ?? 0;
        running.ms += res.elapsed_ms ?? 0;
        setTotals({ ...running });
        if (res.done) { toast.success(`Dry run complete: ${running.styles} styles, ${running.locales} locales`); break; }
        offset = res.next_offset ?? offset + (res.processed ?? 0);
      }
    } finally {
      setRunning(false);
    }
  };

  const runSync = async () => {
    if (!confirm("Run a full CSL styles sync now? This will overwrite all stored .csl files and refresh the catalog.")) return;
    setRunning(true);
    setBatches([]);
    setTotals({ styles: 0, locales: 0, skipped: 0, ms: 0 });
    let offset = 0;
    let commitSha: string | undefined;
    const collected: BatchResult[] = [];
    const running = { styles: 0, locales: 0, skipped: 0, ms: 0 };
    try {
      while (true) {
        const res = await invoke({ offset, limit, commit_sha: commitSha });
        collected.push(res);
        setBatches([...collected]);
        if (!res.ok && res.error) { toast.error(`Batch failed: ${res.error}`); break; }
        commitSha = res.commit;
        running.styles += res.counts?.stylesThisBatch ?? 0;
        running.locales += res.counts?.localesThisBatch ?? 0;
        running.skipped += res.counts?.skippedParseFail ?? 0;
        running.ms += res.elapsed_ms ?? 0;
        setTotals({ ...running });
        if ((res.upload_failures?.length ?? 0) > 0) {
          toast.warning(`${res.upload_failures!.length} upload failure(s) in batch at offset ${offset}`);
        }
        if (res.done) {
          toast.success(`Sync complete: ${running.styles} styles, ${running.locales} locales in ${(running.ms / 1000).toFixed(1)}s`);
          break;
        }
        offset = res.next_offset ?? offset + (res.processed ?? 0);
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> CSL Style Repository sync
        </CardTitle>
        <CardDescription>
          Pulls the full Zotero CSL style repository from GitHub into the private <code>csl-styles</code> bucket
          and refreshes <code>csl_styles_catalog</code>. Runs in resumable batches because edge functions have a
          tight CPU budget. Quarterly cadence is enough.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label htmlFor="csl-limit">Batch size</Label>
            <Input
              id="csl-limit"
              type="number"
              min={50}
              max={1000}
              value={limit}
              onChange={(e) => setLimit(Math.max(50, Math.min(1000, Number(e.target.value) || 300)))}
              className="w-28"
              disabled={running}
            />
          </div>
          <Button variant="outline" onClick={runDryRun} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Dry run
          </Button>
          <Button onClick={runSync} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sync now
          </Button>
        </div>

        {(batches.length > 0 || running) && (
          <div className="rounded-md border p-3 space-y-2">
            <div className="font-medium">
              Totals: {totals.styles} styles · {totals.locales} locales · {totals.skipped} parse-skipped · {(totals.ms / 1000).toFixed(1)}s
            </div>
            <div className="text-xs text-muted-foreground max-h-48 overflow-y-auto font-mono">
              {batches.map((b, i) => (
                <div key={i}>
                  #{i + 1} offset={b.offset} processed={b.processed} done={String(b.done)}
                  {b.error ? ` ERROR: ${b.error}` : ""}
                  {(b.upload_failures?.length ?? 0) > 0 ? ` ⚠ ${b.upload_failures!.length} upload fail` : ""}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
